import { MenuTitles, Pages } from 'constants/menu';
import { ContractSubPage } from '../ContractSubPage';
import TerminateContractAction from '../TerminateContractAction';
import { getContractInformationSubPages } from './ContractInformationSubPages';
import { AddSpecialConditionButton } from './modals/specialConditions/AddSpecialConditionButton';
import { useSelectedContract } from 'hooks';
import {
  FormCondition,
  RenderedSpecialCondition,
  RenderedSpecialConditionConditionType,
  SpecialConditionData,
  useConstructConditionClauseText,
  useInvalidateUseStaffGetSpecialConditions,
  useStaffGetSpecialConditions,
  useStaffUpdateContractSpecialConditions,
} from '@urbanx/agx.campaigns.client';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { isDirtyEqual } from 'utils/compares';
import LoadingState from 'components/loadingState/LoadingState';
import { AgxEmptyState, useToast } from '@urbanx/agx.ui-components2';
import SpecialConditionCollapsiblePanel from './panels/SpecialConditionCollapsiblePanel';
import { RexDisconnectedModalFields } from 'pages/campaigns/components/manageCampaign/RexDataUpdateHelper';
import { useGetListingByCampaignId } from '@urbanx/agx.rex.client';
import { isEqual } from 'lodash-es';
import useRexDisconnectedModal from 'pages/campaigns/components/manageCampaign/useRexDisconnectedModal';
import { withUpdatedSpecialConditionDueDate } from './specialConditionDueDate';
import { getModelFromCustomCondition } from './modals/specialConditions/getModelFromCustomCondition';

const getRenderedCustomSpecialCondition = (
  specialCondition: SpecialConditionData
): RenderedSpecialCondition => {
  const customCondition = getModelFromCustomCondition(specialCondition);

  if (!customCondition || !specialCondition.overriddenClauseNumber) {
    throw new Error('Custom special condition requires a title and clause number');
  }

  return {
    id: specialCondition.id,
    conditionType: RenderedSpecialConditionConditionType.Custom,
    position: specialCondition.overriddenClauseNumber.toString(),
    title: customCondition.textInput,
    clauses: customCondition.subClauses?.map((clause, index) => ({
      clauseId: clause.clauseId,
      clausePosition: `${specialCondition.overriddenClauseNumber}.${index + 1}`,
      clauseText: clause.textInput,
      dueDate: clause.overriddenDueDate?.referenceDate,
    })),
  };
};

const SpecialConditions = () => {
  const { contract, campaignId } = useSelectedContract();
  const [isSaving, setIsSaving] = useState(false);
  const [isError, setIsError] = useState(false);

  const { data: specialConditionsResponse, isPending } = useStaffGetSpecialConditions(
    campaignId ?? '',
    contract?.contractId ?? ''
  );
  const invalidateStaffGetSpecialConditions = useInvalidateUseStaffGetSpecialConditions();

  const [localSpecialConditions, setLocalSpecialConditions] = useState<
    SpecialConditionData[] | undefined
  >(specialConditionsResponse?.specialConditionsData);

  const { modal: rexDisconnectedModal, openWithChanges } = useRexDisconnectedModal();

  const { pushToast } = useToast();

  const { constructConditionClauseText, constructConditionClauseTextPending } =
    useConstructConditionClauseText({
      onSuccess: (conditionTextBlocks) => {
        setRenderedSpecialConditions(conditionTextBlocks);
      },
      onError: async () => {
        pushToast({
          message: 'Failed to construct condition text blocks',
          type: 'error',
        });
        await invalidateStaffGetSpecialConditions(campaignId ?? '', contract?.contractId ?? '');
      },
    });

  const [renderedSpecialConditions, setRenderedSpecialConditions] = useState<
    RenderedSpecialCondition[] | undefined
  >(specialConditionsResponse?.renderedConditions);

  useEffect(() => {
    if (!specialConditionsResponse) return;

    if (
      localSpecialConditions !== undefined &&
      !isDirtyEqual(localSpecialConditions, specialConditionsResponse.specialConditionsData)
    )
      return;

    setLocalSpecialConditions(specialConditionsResponse.specialConditionsData);
    setRenderedSpecialConditions(specialConditionsResponse.renderedConditions);
  }, [localSpecialConditions, specialConditionsResponse]);

  useEffect(() => {
    return () => {
      if (!campaignId || !contract?.contractId) return;

      void invalidateStaffGetSpecialConditions(campaignId, contract.contractId);
    };
  }, [campaignId, contract?.contractId, invalidateStaffGetSpecialConditions]);

  const { data: rexListing, isPending: isPendingRexListing } = useGetListingByCampaignId(
    campaignId || '',
    campaignId !== undefined
  );

  const { staffUpdateContractSpecialConditions } = useStaffUpdateContractSpecialConditions({
    onSuccess: () => {},
    onError: async () => {
      pushToast({
        message: 'Failed to update special conditions',
        type: 'error',
      });
      await invalidateStaffGetSpecialConditions(campaignId ?? '', contract?.contractId ?? '');
    },
  });

  const validateRexModalChangedFields = useCallback(
    (updatedConditions: SpecialConditionData[]) => {
      if (!rexListing && isPendingRexListing === false) {
        const specialConditionsChanged = !isEqual(
          updatedConditions,
          specialConditionsResponse?.specialConditionsData
        );

        openWithChanges(
          specialConditionsChanged
            ? [
                {
                  field: RexDisconnectedModalFields.ExpectedUnconditionalDate,
                  value: '',
                },
              ]
            : []
        );
      }
    },
    [
      rexListing,
      isPendingRexListing,
      openWithChanges,
      specialConditionsResponse?.specialConditionsData,
    ]
  );

  const onConditionDeleted = useCallback(
    (conditionId: string) => {
      if (!conditionId) return;

      const updatedConditions =
        localSpecialConditions?.filter((condition) => condition.id !== conditionId) ?? [];

      setLocalSpecialConditions(updatedConditions);
      setRenderedSpecialConditions((conditions) =>
        conditions?.filter((condition) => condition.id !== conditionId)
      );
    },
    [localSpecialConditions]
  );

  const onAddCustomCondition = useCallback((customSpecialCondition: SpecialConditionData) => {
    setLocalSpecialConditions((prev) => [...(prev || []), customSpecialCondition]);

    setRenderedSpecialConditions((prev) => [
      ...(prev || []),
      getRenderedCustomSpecialCondition(customSpecialCondition),
    ]);
  }, []);

  const onDueDateUpdated = useCallback(
    async (
      conditionId: string,
      clauseId: string,
      newDueDateCondition: FormCondition | undefined,
      newDueDate: string | undefined
    ) => {
      const updatedConditions = localSpecialConditions?.map((condition) => {
        if (condition.id !== conditionId) return condition;

        return withUpdatedSpecialConditionDueDate(condition, clauseId, newDueDateCondition);
      });

      setLocalSpecialConditions(updatedConditions);

      setRenderedSpecialConditions((prevRendered) =>
        prevRendered?.map((renderedCondition) => {
          if (renderedCondition.id !== conditionId) return renderedCondition;

          return {
            ...renderedCondition,
            clauses: renderedCondition.clauses?.map((clause) => {
              if (clause.clauseId !== clauseId) return clause;

              return {
                ...clause,
                dueDate: newDueDate,
              };
            }),
          };
        })
      );
    },
    [localSpecialConditions]
  );

  const isFormDirty = useMemo(
    () => !isDirtyEqual(localSpecialConditions, specialConditionsResponse?.specialConditionsData),
    [localSpecialConditions, specialConditionsResponse?.specialConditionsData]
  );

  const onSaveChanges = useCallback(
    async (onSuccess: () => void) => {
      const updatedConditions = localSpecialConditions ?? [];

      setIsSaving(true);
      setIsError(false);

      try {
        validateRexModalChangedFields(updatedConditions);
        await staffUpdateContractSpecialConditions({
          contractId: contract?.contractId ?? '',
          body: updatedConditions,
        });
        await constructConditionClauseText({
          campaignId: campaignId ?? '',
          contractId: contract?.contractId ?? '',
          model: updatedConditions,
        });
        await invalidateStaffGetSpecialConditions(campaignId ?? '', contract?.contractId ?? '');
        onSuccess();
      } catch {
        setIsError(true);
      } finally {
        setIsSaving(false);
      }
    },
    [
      campaignId,
      constructConditionClauseText,
      contract?.contractId,
      invalidateStaffGetSpecialConditions,
      localSpecialConditions,
      staffUpdateContractSpecialConditions,
      validateRexModalChangedFields,
    ]
  );

  if ((isPending || constructConditionClauseTextPending) && !renderedSpecialConditions) {
    return <LoadingState />;
  }

  return (
    <ContractSubPage
      pageTitle="Contract Information"
      secondaryActiveMenu={MenuTitles.CONTRACT_INFORMATION}
      currentPage={Pages.SPECIAL_CONDITIONS}
      subPages={getContractInformationSubPages()}
      onSaveChanges={(onSuccess) => {
        void onSaveChanges(onSuccess);
      }}
      isFormDirty={isFormDirty}
      isSaving={isSaving}
      isError={isError}
      secondaryActions={<TerminateContractAction />}
    >
      {!renderedSpecialConditions || renderedSpecialConditions.length === 0 ? (
        <AgxEmptyState variant="minimal" label="No special conditions added." />
      ) : null}

      {renderedSpecialConditions?.map((renderedCondition) => (
        <SpecialConditionCollapsiblePanel
          renderedSpecialCondition={renderedCondition}
          specialCondition={localSpecialConditions?.find(
            (condition) => condition.id === renderedCondition.id
          )}
          onDeleteCondition={onConditionDeleted}
          key={renderedCondition.id ?? renderedCondition.conditionType}
          campaignId={campaignId ?? ''}
          contractId={contract?.contractId ?? ''}
          onDueDateUpdated={onDueDateUpdated}
        />
      ))}
      <AddSpecialConditionButton
        key="add-special-condition-button"
        id="add-special-condition-button"
        label="Add Special Conditions"
        existingConditions={localSpecialConditions || []}
        onAddSpecialCondition={onAddCustomCondition}
        campaignId={campaignId ?? ''}
      />
      {rexDisconnectedModal}
    </ContractSubPage>
  );
};

export default SpecialConditions;
